// apps/roleforge-app/src/components/chat/ChatComposer.tsx
// Plan 3b-ii — Phase 1.2: Message input composer with optional voice transcription.
//
// Issue #1216 (same defect class as the approval card): the send control was
// `bg-plum text-paper`, and `text-paper` on coral is only 2.7:1 on the light
// canvas — under the 3:1 floor for a glyph-only control. Near-black on coral
// is 6.3:1 in both themes, and the target is now a full 44px.

import { useId, useRef, useState, useCallback } from 'react';
import { SendHorizonal } from 'lucide-react';
import { MicButton } from '@vibecontrol/ui-patterns';

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  transcribe?: (audio: Blob) => Promise<string>;
}

export function ChatComposer({ onSend, disabled = false, placeholder = 'Type a message…', transcribe }: Props) {
  const [text, setText] = useState('');
  // #1307: a REAL label, not an aria-label sprinkle. This is the single control
  // the entire guided interview runs through, and it had only a `placeholder` —
  // which is not a label, disappears the moment anything is typed, and was also
  // being used to carry the DISABLED REASON, so a screen-reader user got the
  // reason only if the field happened to be empty. The label is now permanent
  // and visually hidden; the disabled reason is separate, announced live, and
  // linked with `aria-describedby`.
  const [micError, setMicError] = useState<string | null>(null);
  const reactId = useId();
  const textareaId = `rf-composer-${reactId}`;
  const statusId = `rf-composer-status-${reactId}`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The placeholder doubles as the disabled reason at several call sites, so
  // when the composer is disabled that text is real information, not a hint.
  const disabledReason = disabled ? placeholder : null;
  const statusMessage = micError ?? disabledReason;

  const send = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
    // Re-focus after clearing so the user can keep typing.
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [text, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send],
  );

  const handleTranscript = useCallback(
    (transcript: string) => {
      setMicError(null);
      setText((prev) => (prev ? prev + ' ' + transcript : transcript));
      textareaRef.current?.focus();
    },
    [],
  );

  // #1307: `MicButton` accepts `onError` and this call site omitted it, so a
  // failed recording (denied permission, unsupported browser, transcription
  // error) surfaced ONLY in the button's `title` tooltip — invisible on touch,
  // invisible to a screen reader, and invisible to anyone not hovering. AAB's
  // composer already wires this correctly.
  const handleMicError = useCallback((message: string) => {
    setMicError(message);
  }, []);

  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-line bg-paper px-4 py-3">
      <div className="flex items-end gap-2">
        <label className="sr-only" htmlFor={textareaId}>
          Your reply
        </label>
        <textarea
          id={textareaId}
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={statusMessage ? statusId : undefined}
          aria-invalid={micError ? true : undefined}
          rows={1}
          // #1308: `text-sm` is 14px, which iOS Safari auto-zooms on focus and
          // never zooms back out on blur. 16px (`text-base`) on touch-sized
          // viewports, 14px from `sm:` up where there is no auto-zoom.
          className="flex-1 resize-none overflow-y-auto max-h-40 bg-transparent text-base sm:text-sm text-ink placeholder:text-graphite outline-none focus:outline-none rounded-2xl border border-line bg-paper px-4 py-3 focus:border-plum"
        />
        {transcribe && (
          <MicButton
            label="dictation"
            onTranscript={handleTranscript}
            transcribe={transcribe}
            onError={handleMicError}
            disabled={disabled}
            disabledTitle={disabledReason ?? undefined}
            size="md"
          />
        )}
        <button
          type="button"
          onClick={send}
          disabled={disabled || !text.trim()}
          aria-label="Send message"
          className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-plum text-[#0B0E14] disabled:opacity-40 hover:opacity-90 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
        >
          <SendHorizonal size={18} />
        </button>
      </div>
      {/* #1307: state text lives in a live region so it is ANNOUNCED, rather
          than only being drawn (or hidden in a tooltip). */}
      {statusMessage && (
        <p
          id={statusId}
          role="status"
          aria-live="polite"
          className={`text-xs ${micError ? 'text-ember' : 'text-graphite'}`}
        >
          {statusMessage}
        </p>
      )}
    </div>
  );
}
